import { TaskSubTicketRef } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/refs';

import { ToastUtils } from '@app/data/utils';
import { Intent } from '@blueprintjs/core';

import { fetchTask } from '../../../store/effects';

import {
    fetchSubTicketDetail,
    fetchSubTicketStatusLog,
    fetchSubTicketSummary,
    fetchSubTickets,
} from './effects';
import { detailKey, listKey, nextGeneration, summaryKey } from './requestGeneration';

export interface RefreshScope {
    taskId: ID;
    isArchivedShown: boolean;
    // Refresh this sub-ticket's detail too, when its panel is open
    expandedSubTicketId?: ID | null;
}

// Per-view truth. A mutation that commits and then fails to refresh is a different outcome
// from one that fully succeeded, and the user has to be told which they got — swallowing the
// difference is what makes a stale screen look authoritative.
export interface ViewRefreshOutcome {
    list: boolean;
    summary: boolean;
    // null when no panel was open, so "not attempted" is distinguishable from "failed"
    detail: boolean | null;
    ok: boolean;
}

// Every read here is issued under ITS OWN Request's key. A mutation that resolves after the user
// has navigated to another Request therefore advances only the generation of the Request it
// belongs to; combined with the scope-owner guard in the reducers, its reply is dropped rather
// than marking the Request now on screen unsettled or repopulating it with someone else's rows.
export const refreshSubTicketViews = async ({
    taskId,
    isArchivedShown,
    expandedSubTicketId = null,
}: RefreshScope): Promise<ViewRefreshOutcome> => {
    const listPromise = fetchSubTickets({
        filter: { task_id: taskId, include_archived: isArchivedShown ? '1' : '0' },
        generation: nextGeneration(listKey(taskId)),
    })
        .then(() => true)
        .catch(() => false);

    // Summary is aside-scoped on purpose: this refresh has to survive the page unmounting
    const summaryPromise = fetchSubTicketSummary({
        filter: { task_id: taskId },
        generation: nextGeneration(summaryKey(taskId)),
    })
        .then(() => true)
        .catch(() => false);

    const detailPromise: Promise<boolean | null> = expandedSubTicketId
        ? fetchSubTicketDetail({
              taskId,
              id: expandedSubTicketId,
              generation: nextGeneration(detailKey(taskId, expandedSubTicketId)),
          })
              .then(() => true)
              .catch(() => false)
        : Promise.resolve(null);

    // The parent Request's state log shows sub-task status transitions beside its own, so a
    // status change has to reach it too. Deliberately not part of `ok`: the timeline is
    // supplementary, and a Request whose header is collapsed should not report a failed refresh
    // because of a panel nobody is looking at.
    const statusLogPromise = fetchSubTicketStatusLog({ filter: { task_id: taskId } }).catch(() => false);

    const [list, summary, detail] = await Promise.all([
        listPromise,
        summaryPromise,
        detailPromise,
        statusLogPromise,
    ]);

    return { list, summary, detail, ok: list && summary && detail !== false };
};

export interface RefReconcileOutcome {
    participationAutoAdded: boolean;
    // null when there was nothing to refresh
    participantsRefreshed: boolean | null;
    dueAfterParentDeadline: boolean;
}

// Surfaces the two facts every command returns that the caller has to be told rather than left
// to discover, and repairs the parent Task store when one of them changed it.
//
// Takes a LIST of refs because a save can legitimately commit two endpoints — reassign and
// update — and each returns its own ref. Collapsing them with `a ?? b` would silently discard
// whichever flags the losing ref carried, so the facts are merged and the parent participants
// are repaired exactly once.
export const reconcileSubTicketRefs = async (
    refs: (TaskSubTicketRef | null)[],
    taskId: ID
): Promise<RefReconcileOutcome> => {
    const committed = refs.filter((ref): ref is TaskSubTicketRef => ref != null);

    const participationAutoAdded = committed.some((ref) => ref.is_assignee_participation_added);
    const dueAfterParentDeadline = committed.some((ref) => ref.is_due_after_parent_deadline);

    let participantsRefreshed: boolean | null = null;

    if (participationAutoAdded) {
        // The designer was added to the parent Request as a side effect. The participant list is
        // refetched rather than patched locally: the resource carries a role and team
        // participation this response does not, and inventing them would show the Participants
        // card a person who does not match the server. Nobody is ever removed.
        try {
            await fetchTask(taskId);
            participantsRefreshed = true;

            // The auto-add is a real change to the parent Request the caller did not ask for
            // directly, so it is always stated rather than left to be noticed
            ToastUtils.showToast({
                message: 'The designer was also added to this Request as a participant.',
                intent: Intent.PRIMARY,
            });
        } catch (e) {
            console.error(e);
            participantsRefreshed = false;
        }
    }

    if (dueAfterParentDeadline) {
        ToastUtils.showToast({
            message: 'Saved. This phase is due after the deadline of the parent Request.',
            intent: Intent.WARNING,
        });
    }

    return { participationAutoAdded, participantsRefreshed, dueAfterParentDeadline };
};

// Single-ref convenience over the same merge
export const reconcileSubTicketRef = (ref: TaskSubTicketRef, taskId: ID): Promise<RefReconcileOutcome> =>
    reconcileSubTicketRefs([ref], taskId);

export const showSubTicketSuccess = (message: string): void => {
    ToastUtils.showToast({ message, intent: Intent.SUCCESS });
};

export const showSubTicketFailure = (message: string): void => {
    ToastUtils.showToast({ message, intent: Intent.DANGER });
};

// ------------------------------------------------------------------- stale-read recovery
//
// A write that committed while a read-back failed leaves exactly two things repairable, and each
// is repaired by a DIFFERENT endpoint:
//
//   'views'        — refreshSubTicketViews: the list, the aside summary and any open detail panel
//   'participants' — fetchTask: the parent Request's Participants card
//
// They are never interchangeable. Refreshing the views would report success while leaving
// Participants exactly as stale as the user found them, and re-reading the Task would leave the
// list exactly as stale. So the endpoint is chosen from the target INSIDE this module: a caller
// hands over a target and a scope, and cannot wire one target to the other's endpoint.
export type SubTicketRecoveryTarget = 'views' | 'participants';

export interface SubTicketRecoveryScope {
    // Names what already committed, so no retry copy can imply the write itself is in doubt
    what: string;
    taskId: ID;
    refreshViews: () => Promise<ViewRefreshOutcome>;

    // A fact about the committed write that the user needs whether or not the screen could be
    // re-read — the minutes a stop logged, for instance. It is stated in the stale-view message
    // too, because "how much did that save?" is not a question a failed refresh may swallow.
    committedDetail?: string | null;
}

interface RecoveryCopy {
    // Distinct labels on purpose: a retry pointed at the wrong repair is visible in the UI, and
    // provable in a test, rather than being two identical "Retry" buttons
    retryLabel: string;
    offer: (scope: SubTicketRecoveryScope) => string;
    recovered: string;
    stillFailing: string;
}

const RECOVERY_COPY: { [target in SubTicketRecoveryTarget]: RecoveryCopy } = {
    views: {
        retryLabel: 'Retry view refresh',
        offer: ({ what, committedDetail }) =>
            committedDetail
                ? `${what} was saved — ${committedDetail}. The view could not be refreshed, so what you see may be out of date.`
                : `${what} was saved, but the view could not be refreshed. What you see may be out of date.`,
        recovered: 'The view has been refreshed with the current state.',
        stillFailing: 'The view still could not be refreshed. Reload the page to see the current state.',
    },
    participants: {
        retryLabel: 'Retry participants',
        offer: () =>
            'The designer was assigned and added to this Request, but the Participants list could not be refreshed.',
        recovered: 'Participants refreshed.',
        stillFailing: 'Participants still could not be refreshed. Reload the page to see the current list.',
    },
};

// The one place a recovery endpoint is chosen. `views` and `participants` cannot reach one
// another's read from here.
const attemptRecovery = async (target: SubTicketRecoveryTarget, scope: SubTicketRecoveryScope): Promise<boolean> => {
    if (target === 'views') {
        return (await scope.refreshViews()).ok;
    }

    await fetchTask(scope.taskId);

    return true;
};

// One handler for both targets. A resolved failure and a thrown one are the same outcome to the
// user — the thing they asked to repair is still stale — so both are reported, and both re-offer
// the SAME target rather than giving up silently or redirecting the repair.
export const retrySubTicketRecovery = async (
    target: SubTicketRecoveryTarget,
    scope: SubTicketRecoveryScope
): Promise<boolean> => {
    const copy = RECOVERY_COPY[target];

    let isRepaired = false;

    try {
        isRepaired = await attemptRecovery(target, scope);
    } catch (e) {
        console.error(e);
        isRepaired = false;
    }

    if (isRepaired) {
        ToastUtils.showToast({ message: copy.recovered, intent: Intent.SUCCESS });

        return true;
    }

    ToastUtils.showToast({
        message: copy.stillFailing,
        intent: Intent.DANGER,
        timeout: 10000,
        action: {
            // Returns the attempt rather than firing and forgetting it. The toaster ignores the
            // value, but a caller — including a test driving this exact handler — can await the
            // outcome instead of racing it.
            text: copy.retryLabel,
            onClick: () => retrySubTicketRecovery(target, scope),
        },
    });

    return false;
};

// The write committed; only the read back failed. The screen is stale rather than wrong, so this
// is never announced as a plain success — and the retry it offers repairs the named target.
export const showSubTicketRecoveryOffer = (
    target: SubTicketRecoveryTarget,
    scope: SubTicketRecoveryScope
): void => {
    const copy = RECOVERY_COPY[target];

    ToastUtils.showToast({
        message: copy.offer(scope),
        intent: Intent.WARNING,
        timeout: 10000,
        action: {
            text: copy.retryLabel,
            onClick: () => retrySubTicketRecovery(target, scope),
        },
    });
};

export interface SubTicketMutationReport {
    // Announced only when every read-back that was attempted actually landed
    successMessage: string;

    // The real outcome of the view refresh, never a caller's opinion of it. Structural on
    // purpose: a timer write reports the same `ok` under its own outcome type.
    views: { ok: boolean };

    // null when the mutation reconciled no refs at all, so "not attempted" stays distinct
    // from "attempted and failed"
    reconciled: { participantsRefreshed: boolean | null } | null;

    scope: SubTicketRecoveryScope;
}

// The single place a sub-ticket mutation is announced.
//
// A plain success is allowed ONLY when the write committed AND every read-back it triggered
// landed. Anything else is committed-but-stale, and each stale surface gets its own retry
// pointed at its own endpoint. In particular no success toast may PRECEDE those offers: the
// very next toast would contradict it, and the user would be left deciding which one to believe.
export const reportSubTicketMutation = ({
    successMessage,
    views,
    reconciled,
    scope,
}: SubTicketMutationReport): void => {
    const areViewsCurrent = views.ok;
    const areParticipantsCurrent = reconciled?.participantsRefreshed !== false;

    if (areViewsCurrent && areParticipantsCurrent) {
        showSubTicketSuccess(successMessage);

        return;
    }

    if (!areViewsCurrent) {
        showSubTicketRecoveryOffer('views', scope);
    }

    if (!areParticipantsCurrent) {
        showSubTicketRecoveryOffer('participants', scope);
    }
};

export interface SubTicketPartialReport {
    committed: string;
    failed: string;
}

// Update and reassignment are separate endpoints, so a failure after the first has committed is
// a real partial success. Both halves of the truth are stated: which endpoint landed, and
// whether the screen underneath it was actually re-read. Claiming the list is current when its
// refresh failed is the same optimistic fiction the partial report exists to avoid.
export const showSubTicketPartialSuccess = (
    { committed, failed }: SubTicketPartialReport,
    views: ViewRefreshOutcome,
    scope: SubTicketRecoveryScope
): void => {
    const copy = RECOVERY_COPY.views;

    ToastUtils.showToast({
        message: views.ok
            ? `${committed} was saved, but ${failed} failed. The list has been refreshed with the current state.`
            : `${committed} was saved, but ${failed} failed. The view could not be refreshed either, so what you see may be out of date.`,
        intent: Intent.WARNING,
        timeout: 10000,
        ...(views.ok
            ? {}
            : { action: { text: copy.retryLabel, onClick: () => retrySubTicketRecovery('views', scope) } }),
    });
};
